import { useEffect, useMemo, useState } from "react";
import { BsCardChecklist, BsCashStack, BsCheckCircleFill } from "react-icons/bs";
import { LuPencil } from "react-icons/lu";
import { RiWallet3Line } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import NewTableComponent from "../../../components/NewTableComponent";
import Stats from "../../../components/Stats";
import UserAvatar from "../../../components/UserAvatar";
import { apiService } from "../../../services/apiService";
import "../../../styles/Access.css";
import "../../../styles/DriverPayments.css";
import "../../../styles/DriverSafety.css";
import "../../../styles/Issue.css";
import "../../../styles/Services.css";
import "../../../styles/Table.css";
import "../../../styles/TripCostEstimation.css";
import {
  AppMainModules,
  FinancePermission,
  FinanceSubModules,
} from "../../../utils/app-constant";
import { hasPermission } from "../../../utils/helper";
import { errorNotify } from "../../../utils/toast";
import {
  buildDriverPaymentRecordFromApi,
  buildDriverPaymentsOverview,
  buildDriverPaymentTableRows,
  formatAmount,
} from "./driverPayments.utils";

const normalizeText = (value) => String(value ?? "").trim().toLowerCase();
const safeArray = (value) => (Array.isArray(value) ? value : []);
const toNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const buildStatIcon = (icon) => <span className="finance_metric_icon">{icon}</span>;

const matchesSearchTerm = (value, searchText) => {
  if (!normalizeText(searchText)) return true;
  return normalizeText(value).includes(normalizeText(searchText));
};

const toComparableDateValue = (value) => {
  if (!value) return Number.NaN;
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? Number.NaN : parsedDate.getTime();
};

const matchesFilter = (rowValue, filterMeta = {}) => {
  const filterType = filterMeta?.filter_type;
  const filterValue = filterMeta?.value;
  const rowValues = Array.isArray(rowValue) ? rowValue : [rowValue];
  const normalizedRowValues = rowValues.map((value) => normalizeText(value));

  if (!filterType || filterValue === undefined || filterValue === null || filterValue === "") {
    return true;
  }

  if (filterType === "one_of") {
    const filterValues = safeArray(filterValue).map((value) => normalizeText(value));
    return normalizedRowValues.some((value) => filterValues.includes(value));
  }

  if (filterType === "contains") {
    return normalizedRowValues.some((value) => value.includes(normalizeText(filterValue)));
  }

  if (filterType === "equals" || filterType === "equal_to") {
    const normalizedFilterValue = normalizeText(filterValue);
    return normalizedRowValues.some((value) => value === normalizedFilterValue);
  }

  const comparableRowValue = rowValues[0];
  const rowDateValue = toComparableDateValue(comparableRowValue);
  const filterDateValue = toComparableDateValue(filterValue);

  if (!Number.isNaN(rowDateValue) && !Number.isNaN(filterDateValue)) {
    if (filterType === "less_than") return rowDateValue < filterDateValue;
    if (filterType === "greater_than") return rowDateValue > filterDateValue;
    if (filterType === "less_than_equal_to") return rowDateValue <= filterDateValue;
    if (filterType === "greater_than_equal_to") return rowDateValue >= filterDateValue;
  }

  const rowNumberValue = Number(comparableRowValue);
  const filterNumberValue = Number(filterValue);

  if (Number.isFinite(rowNumberValue) && Number.isFinite(filterNumberValue)) {
    if (filterType === "less_than") return rowNumberValue < filterNumberValue;
    if (filterType === "greater_than") return rowNumberValue > filterNumberValue;
    if (filterType === "less_than_equal_to") return rowNumberValue <= filterNumberValue;
    if (filterType === "greater_than_equal_to") return rowNumberValue >= filterNumberValue;
  }

  return true;
};

const DriverPayments = () => {
  const navigate = useNavigate();
  const canCreatePayments = hasPermission({
    permissionSlug: FinancePermission.Create,
    menuSlug: FinanceSubModules.DriverPayments,
    moduleSlug: AppMainModules.finance,
  });
  const canUpdatePayments = hasPermission({
    permissionSlug: FinancePermission.Update,
    menuSlug: FinanceSubModules.DriverPayments,
    moduleSlug: AppMainModules.finance,
  });

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(8);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [paginationMeta, setPaginationMeta] = useState({
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchDriverPayments = async () => {
      setLoading(true);

      try {
        const response = await apiService.getDriverPaymentsPaginated({
          page: currentPage,
          limit,
        });
        const driverPaymentsPageData = response?.data || response || {};
        const pageResults = safeArray(driverPaymentsPageData?.results).map((payment) =>
          buildDriverPaymentRecordFromApi(payment)
        );
        const nextMeta = driverPaymentsPageData?.meta || {};

        if (!isMounted) {
          return;
        }

        setRecords(pageResults);
        setPaginationMeta({
          totalItems: toNumber(
            nextMeta?.totalItems,
            toNumber(driverPaymentsPageData?.total, pageResults.length)
          ),
          totalPages: Math.max(1, toNumber(nextMeta?.totalPages, 1)),
          currentPage: Math.max(1, toNumber(nextMeta?.currentPage, currentPage)),
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setRecords([]);
        setPaginationMeta({
          totalItems: 0,
          totalPages: 1,
          currentPage,
        });
        errorNotify(error || "Failed to fetch driver payments");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDriverPayments();

    return () => {
      isMounted = false;
    };
  }, [currentPage, limit]);

  useEffect(() => {
    setCurrentPage(1);
  }, [limit, searchFilters, searchText]);

  const rows = useMemo(
    () => buildDriverPaymentTableRows(records),
    [records]
  );

  const filteredRows = useMemo(() => {
    const filterAccessors = {
      paymentIdFilter: (row) => row.paymentId,
      driverFilter: (row) => row.driverName,
      monthFilter: (row) => row.monthLabel,
      baseSalaryFilter: (row) => row.baseSalary,
      allowanceFilter: (row) => row.totalAllowances,
      deductionFilter: (row) => row.totalDeductions,
      netPayFilter: (row) => row.netPay,
      expenseDateFilter: (row) => row.expenseDateValue,
    };

    return rows.filter((row) => {
      const searchSource = [
        row.paymentId,
        row.driverName,
        row.monthLabel,
        row.baseSalary,
        row.totalAllowances,
        row.totalDeductions,
        row.netPay,
        row.expenseDateDisplay,
      ].join(" ");

      if (!matchesSearchTerm(searchSource, searchText)) {
        return false;
      }

      return Object.entries(searchFilters || {}).every(([filterName, filterMeta]) => {
        const accessor = filterAccessors[filterName];
        if (!accessor) return true;
        return matchesFilter(accessor(row), filterMeta);
      });
    });
  }, [rows, searchFilters, searchText]);

  const hasClientSideFilters =
    normalizeText(searchText).length > 0 || Object.keys(searchFilters || {}).length > 0;
  const totalItems = hasClientSideFilters
    ? filteredRows.length
    : Math.max(0, Number(paginationMeta?.totalItems || 0));
  const totalPages = hasClientSideFilters
    ? Math.max(1, Math.ceil(Math.max(1, filteredRows.length) / Math.max(limit, 1)))
    : Math.max(1, Number(paginationMeta?.totalPages || 1));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedRows = useMemo(() => filteredRows, [filteredRows]);

  const overview = useMemo(() => buildDriverPaymentsOverview(records), [records]);

  const statsData = useMemo(
    () => [
      {
        title: "Total Salaries (PKR)",
        value: formatAmount(overview.totalSalaries),
        text: <span>Total Driver Payouts</span>,
        icon: buildStatIcon(<RiWallet3Line />),
      },
      {
        title: "Pending Salary Runs",
        value: formatAmount(overview.pendingRuns),
        text: <span>Awaiting Payments</span>,
        className: "completed",
        icon: buildStatIcon(<BsCardChecklist />),
      },
      {
        title: "Allowances (PKR)",
        value: formatAmount(overview.totalAllowances),
        text: <span>Total Allowances Paid</span>,
        className: "late",
        icon: buildStatIcon(<BsCashStack />),
      },
      {
        title: "Deductions (PKR)",
        value: formatAmount(overview.totalDeductions),
        text: <span>Total Deductions Applied</span>,
        className: "pending-delivery",
        icon: buildStatIcon(<BsCheckCircleFill />),
      },
    ],
    [overview]
  );

  const handleCreateClick = () => {
    navigate("/driver-payments/create");
  };

  const columns = [
    {
      name: "Payment ID",
      selector: (row) => (
        <button
          type="button"
          className="finance_link_button finance_link_text"
          onClick={() => navigate(`/driver-payments/${row.id}`, { state: { record: row } })}
        >
          {row.paymentId}
        </button>
      ),
      tableColumn: true,
      filterName: "paymentIdFilter",
      type: "text",
      minWidth: "180px",
    },
    {
      name: "Driver",
      selector: (row) => (
        <div className="driver_payments_driver_cell">
          <UserAvatar username={row.driverName} image={row.driverImage} marginRight="0px" />
          <span>{row.driverName}</span>
        </div>
      ),
      tableColumn: true,
      filterName: "driverFilter",
      type: "text",
      minWidth: "220px",
    },
    {
      name: "Month",
      selector: (row) => row.monthLabel,
      tableColumn: true,
      filterName: "monthFilter",
      type: "text",
      minWidth: "160px",
    },
    {
      name: "Base Salary (PKR)",
      selector: (row) => formatAmount(row.baseSalary),
      tableColumn: true,
      filterName: "baseSalaryFilter",
      type: "number",
      minWidth: "180px",
    },
    {
      name: "Allowance (PKR)",
      selector: (row) => formatAmount(row.totalAllowances),
      tableColumn: true,
      filterName: "allowanceFilter",
      type: "number",
      minWidth: "170px",
    },
    {
      name: "Deduction (PKR)",
      selector: (row) => formatAmount(row.totalDeductions),
      tableColumn: true,
      filterName: "deductionFilter",
      type: "number",
      minWidth: "170px",
    },
    {
      name: "Net Pay (PKR)",
      selector: (row) => formatAmount(row.netPay),
      tableColumn: true,
      filterName: "netPayFilter",
      type: "number",
      minWidth: "170px",
    },
    {
      name: "Expense Date",
      selector: (row) => row.expenseDateDisplay,
      tableColumn: true,
      filterName: "expenseDateFilter",
      type: "date",
      minWidth: "150px",
    },
    {
      name: "Action",
      selector: (row) => (
        <div className="table_action_btn">
          {canUpdatePayments && (
            <button
              type="button"
              className="edit_btn"
              title="View payment"
              onClick={() => navigate(`/driver-payments/${row.id}`, { state: { record: row } })}
            >
              <LuPencil />
            </button>
          )}
        </div>
      ),
      tableColumn: false,
      minWidth: "100px",
      grow: 0,
      ignoreRowClick: true,
    },
  ];

  return (
    <div className="driver_payments_page trip_cost_estimation_page">
      <Breadcrumbs values={["Finance", "Driver Payments"]} />

      <div className="access_view">
        <h2>Driver Payments</h2>

        <div className="services_buttons">
          {canCreatePayments && (
            <button type="button" onClick={handleCreateClick}>
              Salary Run Status
            </button>
          )}
        </div>
      </div>

      <Stats statsData={statsData} />

      <NewTableComponent
        columns={columns}
        data={paginatedRows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={setLimit}
        totalItems={totalItems}
        totalPages={totalPages}
        onSearch={setSearchText}
        searchValue={searchText}
        loading={loading}
        paginationLoading={loading}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        limitOptions={[8, 10, 20, 50]}
        tableClassName="driver_payments_table"
        noDataComponent={
          <div className="add_trip_suggestion_div driver_payments_empty_state">
            <h4>There is nothing here...</h4>
            <p>Driver payments will appear here once they are available from the API.</p>
            {canCreatePayments && (
              <Button
                submitText="Salary Run Status"
                type="button"
                shouldRow={false}
                isShowCancel={false}
                submitBtn={handleCreateClick}
              />
            )}
          </div>
        }
      />
    </div>
  );
};

export default DriverPayments;
